import { useContext, useEffect } from "react";

import type {
  NotificationApi,
  NotificationEvents,
} from "@core/ports/notifications";
import type { z } from "@core/zod";

import { useLogger } from "../../../logger/index.ts";

import { NotificationContext } from "./context.ts";

export function useNotification<
  Events extends NotificationEvents,
  Event extends keyof Events & string,
>(
  event: Event,
  api: NotificationApi<Events>,
  callback: (data: z.infer<Events[Event]>) => void,
): void {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotification must be used within NotificationContext");
  }

  const logger = useLogger();

  useEffect(() => {
    const listener = (receivedEvent: string, data: unknown): void => {
      if (receivedEvent !== event) {
        return;
      }

      const schema = api.events[event];

      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (!schema) {
        logger.error(`Event ${receivedEvent} not found in API`);

        return;
      }

      const result = schema.safeParse(data);

      if (!result.success) {
        logger.error(`Event ${receivedEvent} data is not valid`);

        return;
      }

      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
      callback(result.data as z.infer<Events[Event]>);
    };

    context.addListener(listener);

    return () => {
      context.removeListener(listener);
    };
  }, [api, callback, context, event, logger]);
}
